import { useState } from "react";
import { useSelector } from "react-redux";
import { useNotifications } from "../../context/Notifications";
import { createContractFileService } from "../../services/contractFile/services";
import { uploadFileToAzure } from "../../utils/azure/azure";
import { useTranslator } from "../../context/translator";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

function ImportFiles({
  modalOpen,
  toggleModal,
  loadFiles,
  clientId,
  contractId,
}) {
  const { addNotification } = useNotifications();
  const { user } = useSelector((state) => state.user);
  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const [percent, setPercent] = useState(0);
  const { translate } = useTranslator();
  const [steps, setSteps] = useState(1);
  const [stepsText] = useState([
    "Reading File",
    "Building files",
    "Saving files",
    "Complete",
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);

  const onChangeFile = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles(selectedFiles);
  };

  const handleSaveFiles = async () => {
    if (!files.length) return;

    setIsLoading(true);
    setSteps(1);
    setPercent(0);

    try {
      setSteps(2);
      setPercent(50);

      const filesWith = await Promise.all(
        files.map(async (file) => {
          return {
            file: file,
            fileName: file.name,
            type: file.type,
          };
        }),
      );

      // simulate API call
      for (let fileData of filesWith) {
        const url = await uploadFileToAzure(fileData.file, fileData.fileName);

        const type = fileData.file.type.split("/");
        const name = fileData.fileName.split(".");

        const response = await createContractFileService({
          user: userProfile?.user,
          data: {
            companyId: company?._id,
            customerId: clientId,
            contractId,
            linkUrl: url,
            name: name[0] ?? fileData.fileName,
            type: type[1] ?? fileData.file.type,
            active: true,
          },
        });

        if (!response?.contractFile?.success) throw new Error("Upload failed");
      }

      setSteps(3);
      setPercent(100);

      addNotification({
        id: Date.now(),
        message: "Files uploaded successfully!",
        kind: "success",
      });
    } catch (error) {
      addNotification({
        id: Date.now(),
        message: "Error uploading files",
        kind: "danger",
      });
    } finally {
      setSteps(4);
    }
  };

  const handleCloseModal = () => {
    setIsLoading(false);
    setFiles([]);
    setPercent(0);
    setSteps(1);
    loadFiles();
    toggleModal();
  };

  if (userLoading || companyLoading) return null;

  return (
    <>
      {modalOpen && (
        <div
          id="modalID"
          tabIndex="-1"
          aria-hidden="true"
          className="fixed top-0 right-0 left-0 z-50 flex justify-center items-center w-full h-full bg-black bg-opacity-50 overflow-y-auto"
        >
          <div className="relative p-4 w-full max-w-2xl max-h-full">
            <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
              <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {translate("import-files")}
                </h3>
                <button
                  type="button"
                  className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                  data-modal-hide="modalID"
                  onClick={handleCloseModal}
                >
                  <svg
                    className="w-3 h-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                  <span className="sr-only">Close modal</span>
                </button>
              </div>
              <div className="p-4 md:p-5">
                {isLoading && (
                  <>
                    <div className="flex justify-between mb-1">
                      <span className="text-base font-medium text-blue-700 dark:text-white">
                        {stepsText[steps - 1]}
                      </span>
                      <span className="text-sm font-medium text-blue-700 dark:text-white">
                        {percent}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full"
                        style={{ width: `${percent}%` }}
                      ></div>
                    </div>
                    {steps === 4 && (
                      <>
                        <div className="flex items-center gap-3 mt-3 justify-end">
                          <button
                            type="button"
                            className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800"
                            onClick={handleCloseModal}
                          >
                            {translate("finish")}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
                {!isLoading && !files.length && (
                  <>
                    <div className="flex items-center justify-center w-full">
                      <label
                        htmlFor="dropzone-file"
                        className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <svg
                            className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 20 16"
                          >
                            <path
                              stroke="currentColor"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                            />
                          </svg>
                          <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                            <span className="font-semibold">
                              {translate("click-upload")}
                            </span>{" "}
                            {translate("drag-drop")}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            .jpg .jpeg .png .pdf .xlsx .xls
                          </p>
                        </div>
                        <input
                          id="dropzone-file"
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf,.xlsx,.xls"
                          multiple
                          className="hidden"
                          onChange={onChangeFile}
                        />

                        {/*jpeg, png, pdf, xlsx, xls */}
                      </label>
                    </div>
                  </>
                )}
                {files?.length > 0 && !isLoading && (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800"
                      onClick={handleSaveFiles}
                      disabled={isLoading}
                    >
                      {translate("save-files")}
                    </button>
                    <div className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                      {files.map((file, index) => (
                        <p key={index}>{file.name}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ImportFiles;
